import type { IconButtonProps } from '@mui/material/IconButton';

import { m } from 'framer-motion';
import { useBoolean } from 'minimal-shared/hooks';
import { useMemo, useState, useCallback } from 'react';

import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Badge from '@mui/material/Badge';
import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';

import {
  deleteNotification,
  useGetNotifications,
  markNotificationAsRead,
  createTestNotification,
  markAllNotificationsAsRead,
} from 'src/actions/notifications';

import { useTranslate } from 'src/locales';

import { Label } from 'src/components/label';
import { toast } from 'src/components/snackbar';
import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';
import { varTap, varHover, transitionTap } from 'src/components/animate';

import { useMockedUser } from 'src/auth/hooks';

import { NotificationItem } from './notification-item';

// ----------------------------------------------------------------------

export type NotificationsDrawerProps = IconButtonProps;

export function NotificationsDrawer({ sx, ...other }: NotificationsDrawerProps) {
  const { t } = useTranslate('layout');

  const TABS = useMemo(
    () => [
      { value: 'all', label: t('notificationsDrawer.tabs.all') },
      { value: 'critical', label: t('notificationsDrawer.tabs.critical') },
      { value: 'action_required', label: t('notificationsDrawer.tabs.actionRequired') },
    ],
    [t]
  );

  const { value: open, onFalse: onClose, onTrue: onOpen } = useBoolean();

  const { user } = useMockedUser();

  const [currentTab, setCurrentTab] = useState('all');

  const handleChangeTab = useCallback((event: React.SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);
  }, []);

  // Fetch notifications from API
  const { notifications, notificationsLoading } = useGetNotifications({
    limit: 100,
    unreadOnly: false,
  });

  // Filter notifications based on selected tab
  const filteredNotifications = useMemo(() => {
    if (!notifications) return [];

    if (currentTab === 'critical') {
      return notifications.filter((item) => item.priority === 'high');
    }
    if (currentTab === 'action_required') {
      return notifications.filter((item) => item.priority === 'high' || item.priority === 'medium');
    }
    return notifications;
  }, [currentTab, notifications]);

  // Calculate counts for tabs
  const tabCounts = useMemo(() => {
    if (!notifications) {
      return { all: 0, critical: 0, action_required: 0 };
    }
    const all = notifications.length;
    const critical = notifications.filter((item) => item.priority === 'high').length;
    const actionRequired = notifications.filter(
      (item) => item.priority === 'high' || item.priority === 'medium'
    ).length;
    return { all, critical, action_required: actionRequired };
  }, [notifications]);

  const totalUnRead = notifications?.filter((item) => item.isUnRead === true).length || 0;

  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead();
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleMarkAsRead = async (id: string) => {
    try {
      await markNotificationAsRead(id);
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteNotification(id);
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const handleCreateTestNotification = async () => {
    try {
      await createTestNotification();
      toast.success(t('notificationsDrawer.testNotificationCreated'));
    } catch (error) {
      console.error('Failed to create test notification:', error);
      toast.error(t('notificationsDrawer.testNotificationError'));
    }
  };

  const renderHead = () => (
    <Box
      sx={{
        py: 2,
        pr: 1,
        pl: 2.5,
        minHeight: 68,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        {t('notificationsDrawer.title')}
      </Typography>

      {!!totalUnRead && (
        <Tooltip title={t('notificationsDrawer.markAllAsRead')}>
          <IconButton color="primary" onClick={handleMarkAllAsRead}>
            <Iconify icon="eva:done-all-fill" />
          </IconButton>
        </Tooltip>
      )}

      <IconButton onClick={onClose} sx={{ display: { xs: 'inline-flex', sm: 'none' } }}>
        <Iconify icon="mingcute:close-line" />
      </IconButton>
    </Box>
  );

  const renderTabs = () => (
    <Tabs variant="fullWidth" value={currentTab} onChange={handleChangeTab} indicatorColor="custom">
      {TABS.map((tab) => (
        <Tab
          key={tab.value}
          iconPosition="end"
          value={tab.value}
          label={tab.label}
          icon={
            <Label
              variant={((tab.value === 'all' || tab.value === currentTab) && 'filled') || 'soft'}
              color={
                (tab.value === 'critical' && 'error') ||
                (tab.value === 'action_required' && 'warning') ||
                'default'
              }
            >
              {tabCounts[tab.value as keyof typeof tabCounts]}
            </Label>
          }
        />
      ))}
    </Tabs>
  );

  const renderList = () => (
    <Scrollbar>
      <Box component="ul">
        {filteredNotifications?.map((notification) => (
          <Box component="li" key={notification.id} sx={{ display: 'flex' }}>
            <NotificationItem
              notification={notification}
              onMarkAsRead={handleMarkAsRead}
              onDelete={handleDelete}
            />
          </Box>
        ))}
      </Box>
    </Scrollbar>
  );

  return (
    <>
      <IconButton
        component={m.button}
        whileTap={varTap(0.96)}
        whileHover={varHover(1.04)}
        transition={transitionTap()}
        aria-label={t('notificationsDrawer.ariaLabel')}
        onClick={onOpen}
        sx={sx}
        {...other}
      >
        <Badge badgeContent={totalUnRead} color="error">
          <Iconify width={24} icon="solar:bell-bing-bold-duotone" />
        </Badge>
      </IconButton>

      <Drawer
        open={open}
        onClose={onClose}
        anchor="right"
        slotProps={{
          backdrop: { invisible: true },
          paper: { sx: { width: 1, maxWidth: 420 } },
        }}
      >
        {renderHead()}
        {renderTabs()}
        {renderList()}

        <Box sx={{ p: 1 }}>
          <Button fullWidth size="large">
            {t('notificationsDrawer.viewAll')}
          </Button>
        </Box>
      </Drawer>
    </>
  );
}
